import { CustomTableCell, CustomTableHead, FormCheckbox, FormDatePicker, FormFileInput, FormInput, FormSectionHeader, Loader } from "@/components"
import { StepsProps } from "../types"
import { ACK, ACK_AT, ACK_BY, AMOUNT_RECEIVED, CHEQUE_NO, FEE_AMOUNT, FEE_NAME, FEES, IS_SELECTED, RECEIPT_ID, RECEIPT_PROOF, RECEIPTS, RECEIVED_DATE } from "../../consts"
import { Button, Card, CardContent, Table, TableBody, TableHeader, TableRow } from "@/components/ui";
import useStep2 from "./useStep2";
import { FormProvider } from "react-hook-form";
import { cn, formatAmount } from "@/lib/utils";
import { DollarSign, HandCoins, Info, Trash2, Wallet } from "lucide-react";
import moment from "moment";

const Step2 = ({ onCancel, onSubmit: onBaseSubmit, isView, isAddPending }: StepsProps) => {
  const { form, requiredFields, formData, onAddMore, loggedInUserData,
    isAmountReceived, isDataLoading, onRemove, onSubmit, summary, fileKey, isFeeSelected
   } = useStep2({ onBaseSubmit });
  const { errors } = form.formState;

  return (
    <Card className="shadow-none rounded-lg border-0">
      <CardContent>
        <FormSectionHeader className="text-[15px] font-semibold text-g-700 flex flex-col">
          <span>Fees & Payment Collection</span>
          <span className="text-[12px] text-gray-400 font-normal">Record payment details and acknowledge receipt for each fee.</span>
        </FormSectionHeader>
        {isDataLoading ? <Loader /> : 
          <>
            <Card className="shadow-none mb-3 border-0">
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-5 p-0"> 

                <Card className="border-blue-100 shadow-none p-3 mb-3 bg-blue-50 text-primary rounded-md">
                  <CardContent className="flex flex-row gap-3">
                    <div className="flex items-center justify-center h-10 w-10 p-1 rounded-full bg-primary text-white">
                      <DollarSign />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[12px] text-black">Total</span>
                      <span className="text-[18px] font-semibold">{formatAmount(summary?.total || "0")}</span>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-green-100 shadow-none p-3 mb-3 bg-green-50 text-green-600 rounded-md">
                  <CardContent className="flex flex-row gap-3">
                    <div className="flex items-center justify-center h-10 w-10 p-1 rounded-full bg-green-600 text-white">
                      <HandCoins />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[12px] text-black">Received</span>
                      <span className="text-[18px] font-semibold">{formatAmount(summary?.received || "0")}</span>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-amber-100 shadow-none p-3 mb-3 bg-amber-50 text-amber-600 rounded-md">
                  <CardContent className="flex flex-row gap-3">
                    <div className="flex items-center justify-center h-10 w-10 p-1 rounded-full bg-amber-600 text-white">
                      <Wallet />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[12px] text-black">Outstanding</span>
                      <span className="text-[18px] font-semibold">{formatAmount(summary?.outstanding || "0")}</span>
                    </div>
                  </CardContent>
                </Card>
                
              </CardContent>
            </Card>
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 gap-10">
                  <Card className="shadow-none p-0">
                    <CardContent className="p-4">
                    <FormSectionHeader>
                      Select Fee(s)
                      {isFeeSelected ? <span className={cn("text-[12px] px-3 rounded-full", isAmountReceived() ? "bg-green-50 text-green-600" : "bg-amber-100 text-amber-700")}>{isAmountReceived() ? "Received" : "Pending"}</span> : <></>}
                    </FormSectionHeader>
                      <div className="grid grid-cols-1 md:gird-cols-2 lg:grid-cols-4 gap-5 lg:gap-3">
                        {formData[FEES]?.filter((data: any) => data?.active)?.map((data: any, index: number) => {
                          return (
                            <div className="flex flex-row gap-3 items-center">
                              <FormCheckbox 
                                name={`${FEES}.${index}.${IS_SELECTED}`}
                                control={form.control}
                                label=""
                                placeholder="Please enter"
                                errors={(errors as any)?.[FEES]?.[index]?.[IS_SELECTED]?.message}
                                isRequired={requiredFields?.includes(`${FEES}.${index}.${IS_SELECTED}`)}
                                disabled={isView}
                              />
                              <FormInput
                                name={`${FEES}.${index}.${FEE_AMOUNT}`}
                                control={form.control}
                                label={data[FEE_NAME]}
                                placeholder="Please enter"
                                errors={(errors as any)?.[FEES]?.[index]?.[FEE_AMOUNT]?.message}
                                isRequired={requiredFields?.includes(`${FEES}.${index}.${FEE_AMOUNT}`)}
                                disabled={isView}
                                startIcon="$"
                                variant="float"
                              />
                            </div>
                          )
                        })}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="shadow-none p-0">
                    <CardContent className="p-4">
                        <div className="flex flex-row items-center justify-end mb-5">
                          {isAmountReceived() ? <></> : <Button variant="link" className="p-0 ml-5" onClick={onAddMore} type="button" disabled={isView || !isFeeSelected}>+ Add More</Button>}
                        </div>
                        <div className="overflow-x-auto w-full">
                          <div className="min-w-[1000px]">
                            <Table>
                              <TableHeader>
                                <TableRow className="mb-3 rounded">
                                  <CustomTableHead className="w-[150px]">
                                    Cheque Number
                                  </CustomTableHead>
                                  <CustomTableHead className="w-[150px]">
                                    Received Date
                                  </CustomTableHead>
                                  <CustomTableHead className="w-[150px]">
                                    Amount Received
                                  </CustomTableHead>
                                  <CustomTableHead className="w-[150px]">
                                    Receipt Proof
                                  </CustomTableHead>
                                  <CustomTableHead className="w-[150px]">
                                    Acknowledgement
                                  </CustomTableHead>
                                  <CustomTableHead className="w-[150px]">
                                  </CustomTableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {formData?.[RECEIPTS]?.map((_: any, index: number) => {
                                  const hasReceiptId = _[RECEIPT_ID];
                                  const ackBy = _?.[RECEIPTS]?.[index]?.[`${ACK_BY}`] || loggedInUserData?.fullName;
                                  const ackAt = _?.[RECEIPTS]?.[index]?.[`${ACK_AT}`];
                                  return (
                                    <TableRow
                                      key={`row-${index}`}
                                      onClick={(e) => {
                                        // e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                    >
                                      <CustomTableCell>
                                        <FormInput
                                          name={`${RECEIPTS}.${index}.${CHEQUE_NO}`}
                                          control={form.control}
                                          label=""
                                          placeholder="Please enter"
                                          errors={(errors as any)?.[RECEIPTS]?.[index]?.[CHEQUE_NO]?.message}
                                          isRequired={requiredFields?.includes(`${RECEIPTS}.${index}.${CHEQUE_NO}`)}
                                          disabled={isView || hasReceiptId || !isFeeSelected}
                                        />
                                      </CustomTableCell>
                                      <CustomTableCell>
                                        <FormDatePicker
                                          name={`${RECEIPTS}.${index}.${RECEIVED_DATE}`}
                                          control={form.control}
                                          label=""
                                          placeholder="Please enter"
                                          errors={(errors as any)?.[RECEIPTS]?.[index]?.[RECEIVED_DATE]?.message}
                                          isRequired={requiredFields?.includes(`${RECEIPTS}.${index}.${RECEIVED_DATE}`)}
                                          disabled={isView || hasReceiptId || !isFeeSelected}
                                          maxDate={new Date()}
                                        />
                                      </CustomTableCell>
                                      <CustomTableCell>
                                        <FormInput
                                          name={`${RECEIPTS}.${index}.${AMOUNT_RECEIVED}`}
                                          control={form.control}
                                          label=""
                                          placeholder="Please enter"
                                          variant="float"
                                          errors={(errors as any)?.[RECEIPTS]?.[index]?.[AMOUNT_RECEIVED]?.message}
                                          isRequired={requiredFields?.includes(`${RECEIPTS}.${index}.${AMOUNT_RECEIVED}`)}
                                          disabled={isView || hasReceiptId || !isFeeSelected}
                                          startIcon="$"
                                        />
                                      </CustomTableCell>
                                      <CustomTableCell>
                                        <FormFileInput
                                          key={`${fileKey}-${index}`}
                                          name={`${RECEIPTS}.${index}.${RECEIPT_PROOF}`}
                                          control={form.control}
                                          label=""
                                          placeholder="Please enter"
                                          errors={(errors as any)?.[RECEIPTS]?.[index]?.[RECEIPT_PROOF]?.message}
                                          isRequired={requiredFields?.includes(`${RECEIPTS}.${index}.${RECEIPT_PROOF}`)}
                                          disabled={isView || hasReceiptId || !isFeeSelected}
                                          maxSize={10}
                                          accept=".doc,.docx,.csv,.png,.jpg,.jpeg,.txt,.pdf"
                                        />
                                      </CustomTableCell>
                                      <CustomTableCell>
                                        <div className="flex flex-row gap-10 items-center">
                                          <FormCheckbox
                                            name={`${RECEIPTS}.${index}.${ACK}`}
                                            control={form.control}
                                            label={formData[RECEIPTS]?.[index]?.[`${ACK}`] ?
                                              <div className="flex flex-col">
                                                <span className="text-[14px] text-black">Amount Received</span>
                                                <span>{ackBy} {moment(ackAt).format("MM/DD/YYYY HH:mm A")}</span>
                                              </div> : "Confirm Receipt"}
                                            errors={(errors as any)?.[RECEIPTS]?.[index]?.[ACK]?.message}
                                            isRequired={requiredFields?.includes(`${RECEIPTS}.${index}.${ACK}`)}
                                            disabled={isView || hasReceiptId || !isFeeSelected}
                                          />
                                        </div>
                                      </CustomTableCell>
                                      <CustomTableCell>
                                      {!isView && !hasReceiptId && (
                                        <Button
                                          variant="ghost"
                                          type="button"
                                          onClick={onRemove(index)}
                                          disabled={!isFeeSelected}
                                        >
                                          <Trash2 className="stroke-red-500 h-5 w-5" />
                                        </Button>
                                      )}
                                      </CustomTableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                    </CardContent>
                  </Card>
                </div>
                {(errors as any)?.[RECEIPTS]?.message ? <div className="relative text-[10px] text-red-600 whitespace-pre-line">
                  {(errors as any)?.[RECEIPTS]?.message || ""}
                </div> : <></>}
                <div className="flex flex-row gap-3 bg-blue-50 text-primary text-[12px] p-3 my-3 mt-5 rounded-md">
                  <Info className="h-5 w-5" />
                  <span>Acknowledgement confirms receipt of the entered amount. The user and timestamp are recorded for each fee.</span>
                </div>
                <div className="text-[12px] text-gray-500">
                  Receipt Proof: PDF, JPG or PNG . Upto 10 MB
                </div>
                <div className="mt-7 flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="min-w-[110px]"
                    onClick={onCancel}
                    disabled={isAddPending}
                  >
                    Cancel
                  </Button>
                  {isView ? <></> : <Button className="min-w-[90px]" disabled={isAddPending}>
                    {isAddPending ? <Loader variant="secondary" /> : <></>} {Number(summary?.outstanding) ? "Save Draft" :  "Save & Next"}
                  </Button>}
                </div>
              </form>
            </FormProvider>
          </>
        }
    </CardContent>
    </Card>
  )
}

export default Step2;